import React, { useEffect, useState } from "react";
import axios from "axios";

import Loading from "../../components/common/loading";
import {
  AppCard,
  AppPage,
  AppPageHeader,
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from "../../components/common/appShell";
import NotificationManager from "../../utils/notificationManager";

import "./notifications.css";

function authConfig() {
  return {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("token"),
    },
  };
}

function statusClass(status) {
  return `notifications-status notifications-status--${String(
    status || "pending",
  ).toLowerCase()}`;
}

export default function NotificationDispatchHistoryPage(props) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    ruleId: "",
    status: "",
    page: 1,
  });

  const loadHistory = async (nextFilters = filters, keepLoading = true) => {
    if (keepLoading) {
      setLoading(true);
    }
    try {
      const [rulesResponse, historyResponse] = await Promise.all([
        axios.get("/v1/notification-rules", authConfig()),
        axios.get("/v1/notification-dispatches", {
          ...authConfig(),
          params: {
            page: nextFilters.page,
            limit: 25,
            ...(nextFilters.ruleId ? { ruleId: nextFilters.ruleId } : {}),
            ...(nextFilters.status ? { status: nextFilters.status } : {}),
          },
        }),
      ]);
      setRules(Array.isArray(rulesResponse?.data?.data) ? rulesResponse.data.data : []);
      setRows(Array.isArray(historyResponse?.data?.data) ? historyResponse.data.data : []);
      setMeta(historyResponse?.data?.meta || {
        currentPage: 1,
        limit: 25,
        totalCount: 0,
        totalPages: 0,
      });
    } catch (error) {
      NotificationManager.alert("Unable to load notification delivery history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilters = (patch) => {
    const next = {
      ...filters,
      ...patch,
    };
    setFilters(next);
    loadHistory(next, false);
  };

  if (loading) {
    return <Loading black />;
  }

  return (
    <AppPage className="notifications-page">
      <AppPageHeader
        eyebrow="Team"
        title="Notification Delivery History"
        description="Review queued, sent, retried, and failed email/webhook deliveries."
        actions={
          <button
            type="button"
            className="button secondary"
            onClick={() => props.history.push("/team/notifications")}
          >
            Back to Rules
          </button>
        }
      />

      <AppToolbar>
        <AppToolbarGrid>
          <AppToolbarField label="Rule">
            <select
              value={filters.ruleId}
              onChange={(event) =>
                updateFilters({ ruleId: event.target.value, page: 1 })
              }
            >
              <option value="">All rules</option>
              {rules.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
            </select>
          </AppToolbarField>
          <AppToolbarField label="Status">
            <select
              value={filters.status}
              onChange={(event) =>
                updateFilters({ status: event.target.value, page: 1 })
              }
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </AppToolbarField>
        </AppToolbarGrid>
      </AppToolbar>

      <AppCard flush>
        {rows.length === 0 ? (
          <div className="app-empty-state">
            No dispatches match the current filters.
          </div>
        ) : (
          <div className="notifications-table-wrap">
            <table className="notifications-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Rule</th>
                  <th>Destination</th>
                  <th>Device</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                    <td>{row.ruleName || `Rule ${row.ruleId}`}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>
                        {row.destinationName || "Destination"}
                      </div>
                      <div className="notifications-muted">
                        {row.destinationChannel}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{row.deviceName || "Unknown device"}</div>
                      <div className="notifications-muted">
                        Serial {row.deviceSerial || "Unknown"}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700 }}>
                        {row.eventName || "Event"}
                      </div>
                      <div className="notifications-muted">
                        {row.eventState || ""}
                      </div>
                    </td>
                    <td>
                      <span className={statusClass(row.status)}>{row.status}</span>
                    </td>
                    <td>{row.attemptCount}</td>
                    <td>
                      {row.lastError ? (
                        <span className="notifications-error">{row.lastError}</span>
                      ) : row.responseCode ? (
                        <span className="notifications-muted">
                          HTTP {row.responseCode}
                        </span>
                      ) : (
                        <span className="notifications-muted">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AppCard>

      <div className="notifications-footer">
        <div className="notifications-muted">
          Showing {rows.length} of {meta.totalCount} dispatches
        </div>
        <div className="notifications-toolbar">
          <button
            type="button"
            className="button secondary"
            disabled={meta.currentPage <= 1}
            onClick={() => updateFilters({ page: meta.currentPage - 1 })}
          >
            Previous
          </button>
          <button
            type="button"
            className="button secondary"
            disabled={meta.currentPage >= meta.totalPages}
            onClick={() => updateFilters({ page: meta.currentPage + 1 })}
          >
            Next
          </button>
        </div>
      </div>
    </AppPage>
  );
}
